/**
 * "Nytt möte": Klang, Spela in eller Ladda upp.
 *
 * Uppladdningen går direkt från webbläsaren till den signerade URL:en – ljudet
 * passerar aldrig en funktion. Supabase-klienten rapporterar inget förlopp,
 * så vyn visar storlek och förfluten tid i stället för en påhittad procentsats.
 */
import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { Button, Input, SegmentedControl } from '@/components/ui';
import { motenApi } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { formatFranMime, valjInspelningsMime } from '@/hooks/useInspelning';
import type { Kalla, MotesTyp } from '../../../../netlify/functions/_shared/moten/typer';
import { Besked, Etikett, Inbjudningsrad, TypValjare } from './MotesDelar';
import { Inspelare } from './Inspelare';
import { BOTNAMN, FIL_MIME, MAX_FIL_BYTE, felText, klockText, storlekText } from './motesText';

interface Props {
  /** Mötet är skapat och klart att visas. */
  onSkapat: (id: string) => void;
  onAvbryt: () => void;
  /** Ett möte har skapats eller tagits bort utan att vyn bytts – listan ska hämtas om. */
  onListaAndrad: () => void;
  onInspelarAndrad: (aktiv: boolean) => void;
  /** Utskrift av filer påslagen? false = flikarna säger det; undefined = okänt än (ingen notis). */
  sttPa?: boolean;
  /** Klang-kopplingen påslagen? false = Boka/Skicka in nu är avstängda. */
  klangPa?: boolean;
}

const KLANG_AV_NOTIS = 'Klang-kopplingen är inte påslagen än.';

type Skapat = {
  id: string;
  status: string;
  uppladdning?: { path: string; token: string };
};

const FLIKAR: { value: Kalla; label: string }[] = [
  { value: 'klang', label: 'Klang' },
  { value: 'inspelning', label: 'Spela in' },
  { value: 'uppladdning', label: 'Ladda upp' },
];

function idagISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "2026-10-13" → mitt på dagen lokal tid, så att datumet inte glider över midnatt i UTC. */
function datumTillISO(d: string): string | undefined {
  if (!d) return undefined;
  const t = new Date(`${d}T12:00:00`);
  return Number.isNaN(t.getTime()) ? undefined : t.toISOString();
}

function filandelse(namn: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(namn);
  return m ? m[1].toLowerCase() : '';
}

function kontrolleraFil(f: File): string | null {
  const ext = filandelse(f.name);
  if (!FIL_MIME[ext]) return 'Filtypen stöds inte. Välj en mp3-, m4a-, wav-, webm- eller mp4-fil.';
  if (f.size > MAX_FIL_BYTE) return `Filen är ${storlekText(f.size)} – största tillåtna storlek är 500 MB.`;
  if (f.size === 0) return 'Filen är tom.';
  return null;
}

export function NyttMote({ onSkapat, onAvbryt, onListaAndrad, onInspelarAndrad, sttPa, klangPa }: Props) {
  const klangAv = klangPa === false;
  const uid = useId();
  const [flik, setFlik] = useState<Kalla>('klang');
  // Spela in och Ladda upp visas först när tal-till-text är påslaget (sttPa === true):
  // ljudet raderas efter 30 dagar, så ett löfte om utskrift "senare" kan inte hållas.
  const flikar = sttPa === true ? FLIKAR : FLIKAR.filter((f) => f.value === 'klang');
  const [titel, setTitel] = useState('');
  const [typ, setTyp] = useState<MotesTyp>('intern_begransad');
  const [fel, setFel] = useState<string | null>(null);
  const [upptagen, setUpptagen] = useState<null | 'boka' | 'nu' | 'spela' | 'ladda' | 'ta_bort'>(null);

  // Klang
  const [lank, setLank] = useState('');
  const [starttid, setStarttid] = useState('');

  // Uppladdning
  const [datum, setDatum] = useState(idagISO);
  const [fil, setFil] = useState<File | null>(null);
  const [drar, setDrar] = useState(false);
  const [uppladdning, setUppladdning] = useState<{
    id: string;
    path: string;
    token: string;
  } | null>(null);
  const [laddarSedan, setLaddarSedan] = useState<number | null>(null);
  const [sekunder, setSekunder] = useState(0);
  const filRef = useRef<HTMLInputElement>(null);

  // Inspelning
  const [inspelningsId, setInspelningsId] = useState<string | null>(null);

  const lasta = upptagen !== null || inspelningsId !== null || uppladdning !== null;

  // Förfluten tid under uppladdningen.
  useEffect(() => {
    if (laddarSedan === null) return;
    const t = setInterval(() => setSekunder(Math.floor((Date.now() - laddarSedan) / 1000)), 1000);
    return () => clearInterval(t);
  }, [laddarSedan]);

  // Varnar om fliken stängs mitt i en uppladdning.
  useEffect(() => {
    if (laddarSedan === null) return;
    const f = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', f);
    return () => window.removeEventListener('beforeunload', f);
  }, [laddarSedan]);

  function grund(kalla: Kalla, extra: Record<string, unknown>) {
    return { atgard: 'skapa', titel: titel.trim(), typ, kalla, ...extra };
  }

  function kontrolleraTitel(): boolean {
    if (!titel.trim()) {
      setFel('Ge mötet en rubrik.');
      return false;
    }
    return true;
  }

  /* ---------- Klang ---------- */

  async function klang(skickaNu: boolean) {
    setFel(null);
    if (!kontrolleraTitel()) return;
    if (!/^https?:\/\/\S+$/i.test(lank.trim())) {
      setFel('Klistra in mötets länk från Teams, Meet eller Zoom (börjar med https://).');
      return;
    }
    const startISO = starttid ? new Date(starttid).toISOString() : undefined;
    if (!skickaNu && !startISO) {
      setFel('Ange när mötet börjar, eller tryck Skicka in nu om det redan har börjat.');
      return;
    }
    if (!skickaNu && startISO && new Date(startISO).getTime() < Date.now() - 60_000) {
      setFel('Starttiden har redan passerat. Tryck Skicka in nu om mötet pågår.');
      return;
    }
    setUpptagen(skickaNu ? 'nu' : 'boka');
    try {
      const r = await motenApi<Skapat>(
        grund('klang', {
          motes_lank: lank.trim(),
          ...(startISO ? { starttid: startISO } : {}),
          ...(skickaNu && !startISO ? { motesdatum: new Date().toISOString() } : {}),
        }),
      );
      if (skickaNu) {
        // Mötet finns nu. Går boten inte in visar mötesvyn felet och vad man kan göra.
        try {
          await motenApi({ atgard: 'skicka_in_nu', id: r.id });
        } catch {
          /* mötesvyn visar status */
        }
      }
      onSkapat(r.id);
    } catch (e) {
      setFel(felText(e, 'Att spara mötet'));
      setUpptagen(null);
    }
  }

  /* ---------- Uppladdning ---------- */

  function valjFil(f: File | null) {
    setFel(null);
    if (!f) return setFil(null);
    const problem = kontrolleraFil(f);
    if (problem) {
      setFil(null);
      setFel(problem);
      if (filRef.current) filRef.current.value = '';
      return;
    }
    setFil(f);
  }

  function slapp(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDrar(false);
    if (lasta) return;
    valjFil(e.dataTransfer.files?.[0] ?? null);
  }

  async function laddaUpp(mal: { id: string; path: string; token: string }, f: File) {
    setLaddarSedan(Date.now());
    setSekunder(0);
    try {
      const mime = FIL_MIME[filandelse(f.name)];
      // Webbläsarens egen typ kan vara t.ex. audio/x-m4a; klienten lägger filens typ
      // på formulärdelen, så filen får en ren typ (samma data, ingen kopia).
      const ren = new File([f], f.name, { type: mime });
      const { error } = await supabase.storage
        .from('motesljud')
        .uploadToSignedUrl(mal.path, mal.token, ren, { contentType: mime });
      if (error) throw error;
      await motenApi({ atgard: 'ljud_klar', id: mal.id });
      onSkapat(mal.id);
    } catch (e) {
      const t = felText(e, 'Uppladdningen');
      setFel(`Uppladdningen blev inte klar: ${t}`);
      setUpptagen(null);
    } finally {
      setLaddarSedan(null);
    }
  }

  async function startaUppladdning() {
    setFel(null);
    if (!kontrolleraTitel()) return;
    if (!fil) {
      setFel('Välj en ljudfil.');
      return;
    }
    setUpptagen('ladda');
    let mal = uppladdning;
    if (!mal) {
      try {
        const r = await motenApi<Skapat>(
          grund('uppladdning', {
            filandelse: filandelse(fil.name),
            motesdatum: datumTillISO(datum),
          }),
        );
        if (!r.uppladdning) throw new Error('Servern gav ingen uppladdningsadress.');
        mal = { id: r.id, ...r.uppladdning };
        setUppladdning(mal);
      } catch (e) {
        setFel(felText(e, 'Att spara mötet'));
        setUpptagen(null);
        return;
      }
    }
    await laddaUpp(mal, fil);
  }

  /** Uppladdningen misslyckades och användaren ger upp: mötet ska inte bli kvar utan ljud. */
  async function taBortUppladdning() {
    if (!uppladdning) return;
    setUpptagen('ta_bort');
    try {
      await motenApi({ atgard: 'radera', id: uppladdning.id });
      setUppladdning(null);
      setFel(null);
      onListaAndrad();
    } catch (e) {
      setFel(felText(e, 'Att ta bort mötet'));
    } finally {
      setUpptagen(null);
    }
  }

  /* ---------- Inspelning ---------- */

  async function startaInspelning() {
    setFel(null);
    if (!kontrolleraTitel()) return;
    setUpptagen('spela');
    try {
      const r = await motenApi<Skapat>(
        grund('inspelning', {
          format: formatFranMime(valjInspelningsMime()),
          motesdatum: new Date().toISOString(),
        }),
      );
      setInspelningsId(r.id);
    } catch (e) {
      setFel(felText(e, 'Att starta inspelningen'));
    } finally {
      setUpptagen(null);
    }
  }

  const inspelningKlar = useCallback(() => {
    if (inspelningsId) onSkapat(inspelningsId);
  }, [inspelningsId, onSkapat]);

  async function taBortInspelning() {
    if (!inspelningsId) return;
    setUpptagen('ta_bort');
    try {
      await motenApi({ atgard: 'radera', id: inspelningsId });
      setInspelningsId(null);
    } catch (e) {
      setFel(felText(e, 'Att ta bort mötet'));
    } finally {
      setUpptagen(null);
    }
  }

  /* ---------- Vy ---------- */

  const laddar = laddarSedan !== null;

  return (
    <section
      aria-labelledby={`${uid}-rubrik`}
      className="rounded-xl border border-ink-200 bg-surface shadow-card p-5 lg:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 id={`${uid}-rubrik`} className="text-[15px] font-bold text-ink-900">
          Nytt möte
        </h2>
        {inspelningsId === null && !uppladdning && flikar.length > 1 && (
          <SegmentedControl
            label="Hur kommer ljudet in?"
            segments={flikar}
            value={flik}
            onChange={(v) => {
              setFlik(v);
              setFel(null);
            }}
          />
        )}
      </div>

      {inspelningsId ? (
        <div className="space-y-3">
          <p className="text-[13px] text-ink-600">
            <span className="font-semibold text-ink-900">{titel.trim()}</span>
          </p>
          <Inspelare
            key={inspelningsId}
            moteId={inspelningsId}
            onKlar={inspelningKlar}
            onAktiv={onInspelarAndrad}
            onTaBort={() => void taBortInspelning()}
            tarBort={upptagen === 'ta_bort'}
          />
          {typ === 'kund' && <Inbjudningsrad />}
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <Etikett htmlFor={`${uid}-titel`}>Rubrik</Etikett>
            <Input
              id={`${uid}-titel`}
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              maxLength={200}
              placeholder="T.ex. Veckomöte kundtjänst"
              disabled={lasta}
              autoComplete="off"
            />
          </div>

          <TypValjare value={typ} onChange={setTyp} disabled={lasta} namn={`${uid}-typ`} />
          {typ === 'kund' && <Inbjudningsrad />}

          {flik === 'klang' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-4">
                <div className="min-w-0">
                  <Etikett htmlFor={`${uid}-lank`}>Mötets länk</Etikett>
                  <Input
                    id={`${uid}-lank`}
                    type="url"
                    inputMode="url"
                    value={lank}
                    onChange={(e) => setLank(e.target.value)}
                    placeholder="https://teams.microsoft.com/…"
                    disabled={lasta}
                    autoComplete="off"
                  />
                </div>
                <div className="min-w-0">
                  <Etikett htmlFor={`${uid}-start`}>Börjar</Etikett>
                  <Input
                    id={`${uid}-start`}
                    type="datetime-local"
                    value={starttid}
                    onChange={(e) => setStarttid(e.target.value)}
                    disabled={lasta}
                  />
                </div>
              </div>
              <p className="text-[12.5px] text-ink-600 leading-relaxed">
                Boten "{BOTNAMN}" går med i mötet som deltagare vid starttiden – någon i mötet behöver släppa in den.
              </p>
              {klangAv && <Besked ton="info">{KLANG_AV_NOTIS}</Besked>}
              {fel && <Besked ton="fel">{fel}</Besked>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onAvbryt} disabled={lasta}>
                  Avbryt
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void klang(true)}
                  loading={upptagen === 'nu'}
                  disabled={lasta || klangAv}
                >
                  Skicka in nu
                </Button>
                <Button onClick={() => void klang(false)} loading={upptagen === 'boka'} disabled={lasta || klangAv}>
                  Boka
                </Button>
              </div>
            </div>
          )}

          {flik === 'inspelning' && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-ink-600 leading-relaxed">
                För möten i ett rum. Mikrofonen i datorn eller mobilen spelar in, och ljudet laddas upp en minut i taget
                medan mötet pågår. Webbläsaren frågar om lov att använda mikrofonen.
              </p>
              {fel && <Besked ton="fel">{fel}</Besked>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" onClick={onAvbryt} disabled={lasta}>
                  Avbryt
                </Button>
                <Button onClick={() => void startaInspelning()} loading={upptagen === 'spela'} disabled={lasta}>
                  Starta inspelning
                </Button>
              </div>
            </div>
          )}

          {flik === 'uppladdning' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-4">
                <div className="min-w-0">
                  <span
                    className="block text-[11px] font-bold uppercase tracking-[0.12em] text-ink-500 mb-2"
                    id={`${uid}-fil-etikett`}
                  >
                    Ljudfil
                  </span>
                  <label
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!lasta) setDrar(true);
                    }}
                    onDragLeave={() => setDrar(false)}
                    onDrop={slapp}
                    className={cn(
                      'relative flex flex-col justify-center min-h-[84px] rounded-lg border border-dashed px-4 py-3 transition-colors',
                      lasta ? 'cursor-default' : 'cursor-pointer hover:border-ink-400',
                      drar ? 'border-brand-500 bg-brand-50' : 'border-ink-300 bg-surface-sunken',
                      'focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2',
                    )}
                  >
                    <input
                      ref={filRef}
                      type="file"
                      accept=".mp3,.m4a,.wav,.webm,.mp4,audio/mpeg,audio/mp4,audio/wav,audio/webm,video/mp4"
                      className="sr-only"
                      aria-labelledby={`${uid}-fil-etikett`}
                      aria-describedby={`${uid}-fil-hjalp`}
                      disabled={lasta}
                      onChange={(e) => valjFil(e.target.files?.[0] ?? null)}
                    />
                    {fil ? (
                      <>
                        <span className="text-[13.5px] font-semibold text-ink-900 break-all">{fil.name}</span>
                        <span className="text-[12px] text-ink-500 mt-0.5">
                          {storlekText(fil.size)}
                          {!lasta && ' · klicka för att byta fil'}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-[13.5px] font-semibold text-ink-900">Välj fil eller släpp den här</span>
                        <span id={`${uid}-fil-hjalp`} className="text-[12px] text-ink-500 mt-0.5">
                          mp3, m4a, wav, webm eller mp4 · högst 500 MB
                        </span>
                      </>
                    )}
                  </label>
                </div>
                <div className="min-w-0">
                  <Etikett htmlFor={`${uid}-datum`}>Mötesdatum</Etikett>
                  <Input
                    id={`${uid}-datum`}
                    type="date"
                    value={datum}
                    onChange={(e) => setDatum(e.target.value)}
                    disabled={lasta}
                  />
                </div>
              </div>

              {laddar && fil && (
                <div
                  className="rounded-lg border border-ink-200 bg-surface-sunken p-3.5"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
                    <span className="font-semibold text-ink-900">Laddar upp {storlekText(fil.size)}</span>
                    <span className="tabular-nums text-ink-600">{klockText(sekunder)}</span>
                  </div>
                  {/* Obestämd stapel: klienten rapporterar inget förlopp, så ingen procentsats visas. */}
                  <div className="mt-2.5 h-1.5 rounded-full bg-ink-200 overflow-hidden" aria-hidden>
                    <div className="h-full w-1/3 rounded-full bg-brand-500 animate-[motesljud_1.4s_ease-in-out_infinite] motion-reduce:animate-none motion-reduce:w-full motion-reduce:opacity-60" />
                  </div>
                  <p className="mt-2 text-[12px] text-ink-600">Håll fliken öppen tills uppladdningen är klar.</p>
                </div>
              )}

              {fel && <Besked ton="fel">{fel}</Besked>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    // Ett möte som skapades men vars fil inte kom fram ska synas i listan.
                    if (uppladdning) onListaAndrad();
                    onAvbryt();
                  }}
                  disabled={laddar || upptagen !== null}
                >
                  Avbryt
                </Button>
                {uppladdning && !laddar && (
                  <Button
                    variant="secondary"
                    onClick={() => void taBortUppladdning()}
                    loading={upptagen === 'ta_bort'}
                    disabled={upptagen !== null}
                  >
                    Ta bort mötet
                  </Button>
                )}
                <Button
                  onClick={() => void startaUppladdning()}
                  loading={upptagen === 'ladda' && !laddar}
                  disabled={laddar || upptagen !== null || !fil}
                >
                  {laddar ? 'Laddar upp…' : uppladdning ? 'Försök igen' : 'Ladda upp'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
